import React, {
    useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import Select from 'react-select';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import ProBadge from '../../../common/ProBadge/ProBadge';

const StreamSelect = ({ availableStreams, setStream, currentStreamId }) => {
    const [atmosDisclaimer, setAtmosDisclaimer] = useState(false);

    const history = useHistory();
    const options = useMemo(() => availableStreams.map((stream) => {
        const streamName = stream.streamName;
        const imageUrl = stream.imageUrl.replace('400x400', '100x100');
        const pro = stream.pro;

        return ({
            label: (
                <React.Fragment>
                    <div>
                        {pro && <ProBadge className="streamLabelProBadge" title="pro" />}
                        <img src={imageUrl} height="50px" width="50px" alt={streamName} className="streamLabelImage" />
                    </div>
                    {streamName}
                </React.Fragment>),
            value: stream.streamId,
            name: stream.streamName,
            semiPro: stream.semiPro,
            pro,
            color: stream.color,
        });
    }),
    [availableStreams]);

    const selectedOption = options.find((option) => option.value === currentStreamId);

    useEffect(() => {
        if (selectedOption.value === 'antena_1_dolby_atmos') {
            setAtmosDisclaimer('compatible');
        }
    }, [selectedOption]);

    const customStyle = {
        container: (styles) => ({
            ...styles,
            width: 350,
            height: 50,
            '@media(max-width: 765px)': {
                width: 250,
                height: 50,
            },
        }),
        control: (styles) => ({
            ...styles,
            border: '1px solid D0D5DD',
            borderRadius: 0,
            maxWidth: '280px',
            boxShadow: 'none',
            '&:hover': {
                border: `1px solid ${selectedOption.color}`,
            },
            height: 50,
            flexDirection: 'row',
            /*
            background: 'linear-gradient(120deg, white 70%, #cfcfcf 70%, #e8e8e8 71%)',
            */
            cursor: 'pointer',
            '@media(max-width: 767px)': {
                height: 50,
                /*
                background: 'linear-gradient(120deg, white 60%, #cfcfcf 60%, #e8e8e8 61%)',
                */
            },
        }),
        indicatorsContainer: () => ({
            right: -1,
            svg: {
                fill: '#555',
            },
        }),
        loadingIndicator: () => ({
            display: 'none',
        }),
        menu: (styles) => ({
            ...styles,
            border: '1px solid #f2f2f2',
            borderRadius: 0,
            boxShadow: '0 7px 12px rgba(0,0,0,0.6)',
            overflow: 'hidden',
            marginTop: 0,
            textAlign: 'left',
            zIndex: 2,
        }),
        option: (styles, { data, isDisabled, isFocused, isSelected }) => ({
            ...styles,
            height: 70,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'start',
            borderRadius: 0,
            cursor: 'pointer',
            marginTop: 4,
            marginBottom: 4,
            '@media(max-width: 765px)': {
                height: 60,
            },
            background: isDisabled
                ? undefined
                : isSelected
                    ? '#e8e8e8'
                    : isFocused
                        ? `linear-gradient(90deg, ${data.color} 2%, ${data.color}08 2%)` // 08: Hex for 3% alpha
                        : undefined,
            color: isDisabled
                ? undefined
                : isSelected
                    ? '#252D38'
                    : undefined,
        }),
        singleValue: () => ({
            width: 'calc(100% - 8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'start',
            textAlign: 'left',
            borderRadius: 0,
            height: '40px',
            marginRight: 2,
            marginLeft: 2,
            fontSize: 12,
            fontWeight: 900,
            color: '#252D38',
        }),
    };

    function selectStream(event, stat, _history) {
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

        if (isSafari && event.value === 'antena_1_dolby_atmos') {
            setStream(event.value, stat, _history);

            setAtmosDisclaimer('compatible');
        } else if (!isSafari && event.value === 'antena_1_dolby_atmos') {
            setAtmosDisclaimer('incompatible');

            setStream('antena_1', true, _history);
        } else {
            setStream(event.value, stat, _history);
        }
    }

    const renderAtmosDisclaimer = () => {
        if (atmosDisclaimer === 'incompatible') {
            return (
                <div className="atmosDisclaimer">
                    <div className="incompatibleBox">
                        Dolby Atmos não é compatível com este navegador
                    </div>
                </div>
            );
        }

        if (atmosDisclaimer === 'compatible') {
            return (
                <div className="atmosDisclaimer">
                    <div className="compatibleBox">
                        <FontAwesomeIcon icon={faCheckCircle} width="16" tabIndex="0" color="#4fdc66" />
                        DOLBY ATMOS ativado
                    </div>
                </div>
            );
        }

        return null;
    };

    return (
        <div className="selectSource">
            <p className="tw-text-sm tw-font-semibold">
                Escolha o tipo de stream:
            </p>

            <div className="selectBox">
                <Select
                    id="selectStream"
                    instanceId="selectStream"
                    styles={customStyle}
                    value={selectedOption}
                    options={options}
                    onChange={(event) => selectStream(event, true, history)}
                    isSearchable={false}
                    // hideSelectedOptions
                    // The following line remove the radius border from every element on react-select
                    theme={(theme) => ({
                        ...theme,
                        borderRadius: 10,
                    })}
                />
            </div>

            {renderAtmosDisclaimer()}
        </div>
    );
};


StreamSelect.propTypes = exact({
    availableStreams: PropTypes.arrayOf(
        PropTypes.shape({
            streamName: PropTypes.string.isRequired,
            streamId: PropTypes.string.isRequired,
            imageUrl: PropTypes.string,
            color: PropTypes.string,
            semiPro: PropTypes.bool,
            pro: PropTypes.bool,
        }),
    ).isRequired,
    currentStreamId: PropTypes.string.isRequired,
    setStream: PropTypes.func.isRequired,
    history: PropTypes.shape({
        push: PropTypes.func.isRequired,
    }),
});


export default StreamSelect;
